/**
 * OAuth Token Exchange Handler (Browser Cache Implementation)
 *
 * This is a browser-based implementation for development/testing.
 * In production, this should be replaced with server-side endpoint.
 */

/**
 * Validate PKCE code_verifier against code_challenge
 * @param {string} codeVerifier - The code verifier sent by the client
 * @param {string} codeChallenge - The code challenge stored during authorization
 * @returns {Promise<boolean>} - True if validation succeeds
 */
async function validatePKCE(codeVerifier, codeChallenge) {
  try {
    // SHA-256 hash the code_verifier
    const encoder = new TextEncoder()
    const data = encoder.encode(codeVerifier)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)

    // Convert to base64url encoding
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const base64 = btoa(String.fromCharCode.apply(null, hashArray))
    const base64url = base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')

    // Compare with stored code_challenge
    return base64url === codeChallenge
  } catch (error) {
    console.error('PKCE validation error:', error)
    return false
  }
}

/**
 * Exchange authorization code for access token
 * @param {Object} params - Token exchange parameters
 * @param {string} params.code - Authorization code
 * @param {string} params.code_verifier - PKCE code verifier
 * @param {string} params.grant_type - Grant type (must be 'authorization_code')
 * @param {string} params.client_id - Client ID (optional)
 * @param {string} params.redirect_uri - Redirect URI (optional)
 * @returns {Promise<Object>} - Token response or error
 */
export async function exchangeOAuthToken(params) {
  const { code, code_verifier, grant_type, client_id, redirect_uri } = params

  // 1. Validate grant_type
  if (grant_type !== 'authorization_code') {
    return {
      error: 'unsupported_grant_type',
      error_description: 'Only authorization_code grant type is supported',
      status: 400,
    }
  }

  // 2. Validate required parameters
  if (!code || !code_verifier) {
    return {
      error: 'invalid_request',
      error_description: 'Missing required parameters: code and code_verifier',
      status: 400,
    }
  }

  // 3. Look up the code in localStorage cache
  const storedCodes = JSON.parse(localStorage.getItem('oauth_codes') || '{}')
  const authData = storedCodes[code]

  if (!authData) {
    return {
      error: 'invalid_grant',
      error_description: 'Invalid authorization code',
      status: 400,
    }
  }

  // 4. Check if code has expired
  if (Date.now() > authData.expires_at) {
    // Clean up expired code
    delete storedCodes[code]
    localStorage.setItem('oauth_codes', JSON.stringify(storedCodes))

    return {
      error: 'invalid_grant',
      error_description: 'Authorization code has expired',
      status: 400,
    }
  }

  // 5. Check if code has already been used
  if (authData.used) {
    return {
      error: 'invalid_grant',
      error_description: 'Authorization code has already been used',
      status: 400,
    }
  }

  // 6. PKCE validation: Verify code_verifier against code_challenge
  const isValid = await validatePKCE(code_verifier, authData.code_challenge)

  if (!isValid) {
    return {
      error: 'invalid_grant',
      error_description: 'PKCE validation failed - code_verifier does not match code_challenge',
      status: 400,
    }
  }

  // 7. Mark code as used immediately
  authData.used = true
  storedCodes[code] = authData
  localStorage.setItem('oauth_codes', JSON.stringify(storedCodes))

  // 8. Return successful token response
  return {
    access_token: authData.token,
    token_type: 'Bearer',
    expires_in: 3600,
    status: 200,
  }
}

/**
 * Clean up expired OAuth codes from localStorage
 * This should be called periodically or on app initialization
 */
export function cleanupExpiredCodes() {
  try {
    const storedCodes = JSON.parse(localStorage.getItem('oauth_codes') || '{}')
    const now = Date.now()
    let hasChanges = false

    Object.keys(storedCodes).forEach(code => {
      if (storedCodes[code].expires_at < now) {
        delete storedCodes[code]
        hasChanges = true
      }
    })

    if (hasChanges) {
      localStorage.setItem('oauth_codes', JSON.stringify(storedCodes))
    }
  } catch (error) {
    console.error('Error cleaning up expired codes:', error)
  }
}

export default {
  exchangeOAuthToken,
  validatePKCE,
  cleanupExpiredCodes,
}
